import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useFormContext, useWatch } from "react-hook-form";
import ResourceSelect from "../../../../../../../../components/shared/inputs/ResourceSelect";
import Select from "../../../../../../../../components/shared/inputs/Select";
import { useScheduleContext } from "../../../../../hooks/context/useScheduleContext";
import { useDateFilters } from "../../../../../../../../components/shared/filters/DateFilters/context/useDateFilters";
import { useFilterResourceParams } from "../../../../../hooks/filters/useFilterResourceParams";
import { getCommonProperty } from "../../../../../helpers/form/getCommonProperty";
import { getSettingsResourceSearchFilters, renderSettingsResourceLabel } from "../../../../../../../../utils/helpers/settings";
import { unassignedShiftsResource } from "../../../../../helpers/data/unassignedShiftsResource";
import { useUser } from "../../../../../../../../utils/hooks/user";
import { cn } from "../../../../../../../../lib/utils";

type JobSelectProps = {
    value?: any;
    onChange?: (value: any) => void;
    disabled?: boolean;
    className?: string;
};

function JobSelect({ value, onChange, disabled, className }: JobSelectProps) {
    const { t } = useTranslation();
    const { selectedCellOrEvent, type } = useScheduleContext();
    const { mode } = useDateFilters();
    const { control } = useFormContext();
    const users = useWatch({ control, name: "users" });
    const loggedUser = useUser();

    const jobs = useMemo(() => {
        const user = typeof selectedCellOrEvent?.user === "string"
            ? (selectedCellOrEvent as any)?.resource
            : selectedCellOrEvent?.user ?? (type === "home" ? loggedUser : undefined);
        if (mode === "month") {
            return getCommonProperty(
                users ?? [],
                "jobs",
                "jobId",
                ["id", "code", "description", "jobId", "hourlyRate"]
            );
        }
        return user ? getCommonProperty(
            user,
            "jobs",
            "jobId",
            ["id", "code", "description", "jobId", "hourlyRate"]
        ) : [];
    }, [selectedCellOrEvent, mode, users, type, loggedUser]);

    const isUnassigned = selectedCellOrEvent?.isUnassignedShift ||
        selectedCellOrEvent?.resourceId === unassignedShiftsResource.id;

    const onSelectChange = useCallback((code?: string) => {
        onChange?.(jobs?.find((item: any) => item.code === code));
    }, [jobs, onChange]);

    const resourceParams = useFilterResourceParams("job");

    return isUnassigned ? (
        <ResourceSelect
            mode="single"
            resourcePath="/jobs"
            renderLabel={renderSettingsResourceLabel}
            getSearchFilter={getSettingsResourceSearchFilters}
            params={resourceParams}
            placeholder={t("job")}
            disabled={disabled}
            value={value}
            onChange={onChange}
            className={className}
        />
    ) : (
        <Select
            className={cn("tw-w-full", className)}
            placeholder={t("job")}
            disabled={disabled}
            value={value?.code}
            onChange={onSelectChange}
        >
            {jobs?.map((job: any) => (
                <Select.Item key={job.code} value={job.code}>
                    {renderSettingsResourceLabel(job)}
                </Select.Item>
            ))}
        </Select>
    );
}

export default JobSelect;
